import React, { useState, useEffect, useContext, createContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PiMonitorPlayBold } from 'react-icons/pi';
import { SlNotebook } from 'react-icons/sl';
import { BsListTask } from 'react-icons/bs';
import { LiaLaptopCodeSolid } from 'react-icons/lia';
import { AiOutlineFundProjectionScreen } from "react-icons/ai";
import { apiGet, apiPost, apiPut } from "../utils/apiAuth";
import config from '../config';
import CONSTANTS, { ContentTypeConstants } from '../constants/contentCreationConstants';
import CommentPopup from './CommentPopup';
import ClonePopup from './ClonePopup';
import { ConfirmDialog } from './ui';
import InlineHierarchicalFilter from './InlineHierarchicalFilter';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Box, Typography, IconButton, Tooltip, TablePagination, 
  Checkbox, Button, Chip, CircularProgress, Alert, Menu, MenuItem,
  Collapse
} from '@mui/material';
import JoditProEditor from 'jodit-pro-react';
import { 
  Add as AddIcon,
  Comment as CommentIcon,
  FileCopy as FileCopyIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  
} from '@mui/icons-material';

const layoutStyles = {
  container: { minHeight: "100vh", maxWidth: "100vw", overflowX: "hidden", overflowY: "auto" },
  header: { padding: "20px", backgroundColor: "#fff", marginBottom: 0 },
  headerTitle: { color: "#333", fontSize: "28px", fontWeight: 600, margin: 0 },
  filtersWrapper: { backgroundColor: "#fff", padding: "15px 20px", borderBottom: "1px solid #e9ecef" },
};

const filterStyles = {
  group: { minWidth: "150px", marginRight: "15px", marginBottom: "10px" },
  label: { fontWeight: 500, color: "#555", fontSize: "13px", marginBottom: "4px", display: "block" },
  select: { width: "100%", padding: "6px 10px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "13px" },
};

const tableStyles = {
  headerCell: { backgroundColor: "#f8f9fa", fontWeight: 600, color: "#333", borderBottom: "2px solid #dee2e6", fontSize: "13px" },
  actionButton: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px", border: "1px solid #d0d7de", borderRadius: "50%", backgroundColor: "#fff", cursor: "pointer", color: "#495057", position: "relative" },
  addButton: { color: "#28a745", borderColor: "#28a745" },
  cloneIcon: { color: "#6c757d" },
};

const iconStyles = {
  messageCount: { position: "absolute", top: "-6px", right: "-6px", backgroundColor: "#dc3545", color: "#fff", borderRadius: "999px", padding: "0 5px", fontSize: "10px", fontWeight: 600 },
};

const statusBadgeStyles = {
  base: { padding: "4px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 500, textTransform: "uppercase" },
  active: { backgroundColor: "#d4edda", color: "#155724" },
  inactive: { backgroundColor: "#f8d7da", color: "#721c24" },
};

// Video Preview Modal Component
const VideoPreviewModal = ({ isOpen, onClose, video }) => {
  if (!isOpen || !video) return null;

  return (
    <div 
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1060,
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded shadow-lg"
        style={{
          minWidth: '600px',
          maxWidth: '900px',
          maxHeight: '80vh',
          padding: '0',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-semibold">{video.title || 'Video Preview'}</h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        <div className="p-4" style={{ flex: 1, overflowY: 'auto' }}>
          {video.vdo_video_id && video.vdo_otp && video.vdo_playback_info ? (
            <iframe
              src={`https://player.vdocipher.com/v2/?otp=${video.vdo_otp}&playbackInfo=${video.vdo_playback_info}`}
              width="100%"
              height="400"
              frameBorder="0"
              allowFullScreen
              title="Video Player"
              allow="encrypted-media"
              style={{ border: '1px solid #ddd', borderRadius: '4px' }}
            />
          ) : (
            <div style={{ textAlign: "center", color: "#666", padding: "40px" }}>
              <p>Video not available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Notes Preview Modal Component
const NotesPreviewModal = ({ isOpen, onClose, notes }) => {
  if (!isOpen || !notes) return null;

  return (
    <div 
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1060,
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded shadow-lg"
        style={{
          minWidth: '600px',
          maxWidth: '900px',
          maxHeight: '80vh',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-semibold">{notes.title || 'Notes Preview'}</h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        <div className="p-4" style={{ flex: 1, overflowY: 'auto' }}>
          <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
            <Typography component="div" dangerouslySetInnerHTML={{ __html: notes.content || '' }} />
          </Box>
        </div>
      </div>
    </div>
  );
};

// MCQ Preview Modal Component
const MCQPreviewModal = ({ isOpen, onClose, mcq }) => {
  if (!isOpen || !mcq) return null;

  const questionData = mcq.question_data || {};
  const questionText = questionData.question || questionData.Qn || questionData.question_text || 'No question available';
  const options = questionData.options || [];
  const correctAnswer = questionData.correct_answer || '';
  const explanation = questionData.Explanation || 'No explanation available';

  const renderOptions = (optionsArray, correct) => {
    if (!optionsArray || !Array.isArray(optionsArray)) return null;
    return optionsArray.map((option, idx) => {
      const label = String.fromCharCode(65 + idx);
      const isCorrect = option === correct;
      return (
        <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ mr: 1, fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
            {label}.
          </Typography>
          <Typography sx={{ fontWeight: isCorrect ? 600 : 400, color: isCorrect ? '#28a745' : 'inherit' }}>
            {option}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <div 
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1060,
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded shadow-lg"
        style={{
          minWidth: '600px',
          maxWidth: '900px',
          maxHeight: '80vh',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-semibold">MCQ Preview</h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        <div className="p-4" style={{ flex: 1, overflowY: 'auto' }}>
          <Box sx={{ marginBottom: "16px" }}>
            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Question</Typography>
            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
              <Typography>{questionText}</Typography>
            </Box>
          </Box>
          <Box sx={{ marginBottom: "16px" }}>
            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Options</Typography>
            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
              {renderOptions(options, correctAnswer)}
            </Box>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#6c757d', mb: 2, fontSize: '14px' }}>Explanation</Typography>
            <Box sx={{ border: "1px solid #dee2e6", borderRadius: "4px", padding: "12px", backgroundColor: "#fff" }}>
              <Typography>{explanation}</Typography>
            </Box>
          </Box>
        </div>
      </div>
    </div>
  );
};

// Main ProjectContentBank Component
function ProjectContentBank() {
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve projectId from navigation state -> sessionStorage
  const projectId = (
    (location && location.state && location.state.projectId) ||
    (sessionStorage.getItem('projectId') ? JSON.parse(sessionStorage.getItem('projectId')) : null)
  );
  
  // persistence helpers
  const saveState = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
  };
  const loadState = (key, defaultValue = null) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : defaultValue; } catch (e) { return defaultValue; }
  };
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectData, setProjectData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    contentType: 'video',
    approvalStatus: 'All'
  });
  
  // Data states
  const [contents, setContents] = useState([]);
  const [batchAssigned, setBatchAssigned] = useState(false);
  
  // UI states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  
  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    sNo: true,
    phase: true,
    part: true,
    task: true,
    subTask: true,
    topic: true,
    subTopic: true,
    category: true,
    title: true,
    level: true,
    duration: true,
    review: true,
    approvalStatus: true,
    actions: true,
    status: true
  });
  
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Hierarchical filter state
  const [activeHierarchicalFilters, setActiveHierarchicalFilters] = useState({
    subject: '',
    topic: '',
    subTopic: ''
  });
  
  // Modal states
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [videoPreviewModal, setVideoPreviewModal] = useState({ isOpen: false, video: null });
  const [notesPreviewModal, setNotesPreviewModal] = useState({ isOpen: false, notes: null });
  const [mcqPreviewModal, setMcqPreviewModal] = useState({ isOpen: false, mcq: null });
  const [commentPopup, setCommentPopup] = useState({ isOpen: false, referenceType: null, referenceId: null });
  const [clonePopup, setClonePopup] = useState({ isOpen: false, referenceType: null, referenceId: null });
  
  // Check user role
  const isAdmin = () => {
    const userType = sessionStorage.getItem('Type') || '';
    return userType.includes('AD');
  };
  
  const userIsAdmin = isAdmin();
  
  const backButtonCount = useRef(0);
  
  // Handle browser back button - navigate to dashboard after two back clicks
  useEffect(() => {
    const handlePopState = () => {
      backButtonCount.current += 1;
      if (backButtonCount.current >= 2) {
        navigate('/dashboard');
        backButtonCount.current = 0;
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  // Fetch project details when projectId changes; subjects once on mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      setError('No project selected. Open a project from the dashboard or choose one in the filter above.');
      setLoading(false);
      return;
    }

    fetchProjectDetails();
  }, [projectId]);

  // restore saved filters/pagination/columns for this project
  useEffect(() => {
    const saved = loadState(`ProjectContentBank_state_${projectId}`);
    if (saved) {
      if (saved.filters) setFilters(saved.filters);
      if (saved.pagination) setPagination(saved.pagination);
      if (saved.activeHierarchicalFilters) setActiveHierarchicalFilters(saved.activeHierarchicalFilters);
      if (saved.columnVisibility) setColumnVisibility(saved.columnVisibility);
    }
  }, [projectId]);

  // Fetch contents when filters or project change
  useEffect(() => {
    if (project) {
      fetchContents();
    }
  }, [filters, pagination.page, pagination.limit, project, activeHierarchicalFilters]);

  // persist ProjectContentBank state when important UI state changes
  useEffect(() => {
    saveState(`ProjectContentBank_state_${projectId}`, {
      filters,
      pagination,
      activeHierarchicalFilters,
      columnVisibility
    });
  }, [filters, pagination, activeHierarchicalFilters, columnVisibility, projectId]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet(`${config.API_BASE_URL}/api/get-projects/`);
      const projectsList = response.data.projects || [];
      setProjects(projectsList);
      
      const selectedProject = projectsList.find(
        (p) => String(p.id) === String(projectId)
      );
      
      if (!selectedProject) {
        setError('Project not found');
        setProject(null);
        setLoading(false);
        return;
      }
      
      setProject(selectedProject);
      extractProjectHierarchy(selectedProject);
      setLoading(false);
    } catch (err) {
      setError('Failed to load project details');
      setLoading(false);
    }
  };

  const extractProjectHierarchy = (proj) => {
    // Placeholder for future hierarchy extraction from project_data JSON
  };

  const fetchSubjects = async () => {
    try {
      const response = await apiGet(`${config.API_BASE_URL}/api/get-subjects/`);
      setSubjects(response.data.subjects || []);
    } catch (err) {
    }
  };

  const handleApplyHierarchicalFilters = (appliedFilters) => {
    // Update the active filters and close modal
    setActiveHierarchicalFilters({
      subject: appliedFilters.subject || '',
      topic: appliedFilters.topic || '',
      subTopic: appliedFilters.subTopic || ''
    });
    
    // Also update the main filters
    setFilters(prev => ({
      ...prev,
      approvalStatus: appliedFilters.approvalStatus || prev.approvalStatus,
      contentType: appliedFilters.contentType || prev.contentType
    }));

    // Reset pagination
    setPagination(prev => ({ ...prev, page: 1 }));

    // Persist applied hierarchical selections for display

    // If project changed, save pending filters and navigate so the new page can restore and fetch.
    const appliedProject = appliedFilters.project;
    if (appliedProject && String(appliedProject) !== String(projectId)) {
      try {
        sessionStorage.setItem('projectContentBank_pendingFilters', JSON.stringify(appliedFilters));
      } catch (e) {
        // ignore storage errors
      }
      try { sessionStorage.setItem('projectId', JSON.stringify(appliedProject)); } catch(e) {}
      navigate('/project-content', { state: { projectId: appliedProject } });
      return;
    }

    // For same project, updated `filters` will trigger fetch via useEffect
  };

  // Restore pending filters saved before navigation (if any)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('projectContentBank_pendingFilters');
      if (raw) {
        const pending = JSON.parse(raw);
        if (String(pending.project) === String(projectId)) {
          // apply
          setActiveHierarchicalFilters({
            subject: pending.subject || '',
            topic: pending.topic || '',
            subTopic: pending.subTopic || ''
          });
          setFilters(prev => ({
            ...prev,
            approvalStatus: pending.approvalStatus || prev.approvalStatus,
            contentType: pending.contentType || prev.contentType
          }));
          setPagination(prev => ({ ...prev, page: 1 }));
          // clear storage and fetch
          sessionStorage.removeItem('projectContentBank_pendingFilters');
          fetchContents();
        }
      }
    } catch (e) {
      // ignore
    }
  }, [projectId]);

  const fetchContents = async () => {
    try {
      setLoading(true);
      
      const contentTypeMap = {
        [ContentTypeConstants.VIDEO.key]: 'videos',
        [ContentTypeConstants.NOTES.key]: 'contents',
        [ContentTypeConstants.MCQ.key]: 'questions',
        [ContentTypeConstants.CODING.key]: 'questions',
        [ContentTypeConstants.PROJECT_CODING.key]: 'questions'
      };
      
      const ct = String(filters.contentType || '').toLowerCase().replace(/\s+/g, '_');
      const endpointKey = (ct === 'all') ? 'all' : (contentTypeMap[ct] || 'videos');
      const endpointPath = {
        'videos': '/api/get-videos-hierarchical/',
        'contents': '/api/get-contents-hierarchical/',
        'questions': '/api/get-questions-hierarchical/',
        'all': '/api/get-all-content-hierarchical/'
      }[endpointKey] || '/api/get-videos-hierarchical/';

      const requestBody = {
        project_id: parseInt(projectId),
        subject_id: activeHierarchicalFilters.subject || undefined,
        topic_id: activeHierarchicalFilters.topic || undefined,
        sub_topic_id: activeHierarchicalFilters.subTopic || undefined,
        approval_status: filters.approvalStatus !== 'All' ? filters.approvalStatus.toLowerCase() : undefined,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Add question_type for questions endpoint
      if (ct === ContentTypeConstants.MCQ.key) {
        requestBody.question_type = ContentTypeConstants.MCQ.key;
      } else if (ct === ContentTypeConstants.CODING.key) {
        requestBody.question_type = ContentTypeConstants.CODING.key;
      } else if (ct === ContentTypeConstants.PROJECT_CODING.key) {
        requestBody.question_type = ContentTypeConstants.PROJECT_CODING.key;
      }
      
      const response = await apiPost(`${config.API_BASE_URL}${endpointPath}`, requestBody);

      // Determine which field has the content data (including unified 'all' response)
      const contentData = response.data.contents || response.data.videos || response.data.questions || [];
      const totalCount = response.data.total_contents || response.data.total_videos || response.data.total_questions || 0;
      const batchAssignedValue = response.data.batch_assigned || false;
      
      setContents(contentData);
      setBatchAssigned(batchAssignedValue);
      setPagination(prev => ({
        ...prev,
        total: totalCount,
        totalPages: Math.ceil(totalCount / pagination.limit)
      }));
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load contents');
      setLoading(false);
    }
  };

  const handleCheckReview = (content, overrideType) => {
    try {
      // Prefer subject/topic/subtopic from the clicked row, fallback to active hierarchical filters
      const subjectId = content.subject_id || activeHierarchicalFilters.subject;
      const topicId = content.topic_id || activeHierarchicalFilters.topic;
      const subTopicId = content.sub_topic_id || activeHierarchicalFilters.subTopic;

      if (!subjectId) {
        setErrorModal({ isOpen: true, message: 'Please select a subject first' });
        return;
      }

      if (!topicId) {
        setErrorModal({ isOpen: true, message: 'Please select a topic first' });
        return;
      }

      const t = String(overrideType || content.content_type || content.category || filters.contentType || '').toLowerCase();

      if (t === ContentTypeConstants.VIDEO.key) {
        setVideoPreviewModal({ isOpen: true, video: content });
        return;
      }

      if (t === ContentTypeConstants.NOTES.key) {
        setNotesPreviewModal({ isOpen: true, notes: content });
        return;
      }

      if (t === ContentTypeConstants.MCQ.key) {
        setMcqPreviewModal({ isOpen: true, mcq: content });
        return;
      }

      if (t === ContentTypeConstants.PROJECT_CODING.key) {
        const contentId = content.id || content.question_id;
        const subjectName = content.subject_name || (subjects.find(subject => subject.subject_id === subjectId)?.subject_name || '');
        try {
          sessionStorage.setItem('contentCreation_contentType', JSON.stringify(ContentTypeConstants.PROJECT_CODING.key));
          sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(content.level || 'level1'));
          sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(subTopicId || ''));
          sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(topicId));
          sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(String(contentId)));
          sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(subjectId));
          sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
          sessionStorage.setItem('contentCreation_projectId', JSON.stringify(projectId));
          sessionStorage.setItem('contentCreation_batchAssigned', batchAssigned ? 'true' : 'false');
          sessionStorage.setItem('contentCreation_approvalStatus', JSON.stringify(content.approval_status || 'Unknown'));
          sessionStorage.removeItem('contentCreation_isReviewReadOnly');
          if (project && project.project_name) {
            sessionStorage.setItem('contentCreation_projectName', JSON.stringify(project.project_name));
          }
          if (content.description) {
            sessionStorage.setItem('contentCreation_projectCodingDescription', JSON.stringify(content.description));
          } else {
            sessionStorage.removeItem('contentCreation_projectCodingDescription');
          }
          sessionStorage.setItem('contentCreation_freezeFilters', 'true');
          sessionStorage.setItem('contentCreation_navSource', 'project');
        } catch (e) {
          // ignore storage errors
        }
        navigate('/content-creation', {
          state: {
            data: {
              subject_id: subjectId,
              subject_name: subjectName,
              topic_id: topicId,
              sub_topic_id: subTopicId || '',
              content_type: ContentTypeConstants.PROJECT_CODING.key,
              project_id: projectId,
              contentId: String(contentId),
              freezeFilters: true
            }
          }
        });
        return;
      }

      if (t === ContentTypeConstants.CODING.key) {
        // Route based on subject - Python uses existing editor; others use web editor testing route
        const subjectIdLower = String(subjectId).toLowerCase();
        const isPython = subjectIdLower.startsWith('py');

        if (isPython) {
          // Store coding question parameters in sessionStorage
          try {
            sessionStorage.setItem('codingQuestions_subjectId', JSON.stringify(subjectId));
            sessionStorage.setItem('codingQuestions_topicId', JSON.stringify(topicId));
            sessionStorage.setItem('codingQuestions_subTopicId', JSON.stringify(subTopicId || ''));
            sessionStorage.setItem('codingQuestions_level', JSON.stringify(content.level || ''));
            sessionStorage.setItem('codingQuestions_questionId', JSON.stringify(content.question_id));
            sessionStorage.setItem('codingQuestions_editor', JSON.stringify('python'));
            sessionStorage.setItem('codingQuestions_returnPath', '/project-content');
          } catch (e) { /* ignore */ }
          navigate('/coding-questions');
          return;
        }

        // Build testing payload for HTML/CSS/JS editor (single question)
        const q = content || {};
        const qd = q.question_data || {};
        const tabs = qd.Tabs && Array.isArray(qd.Tabs) && qd.Tabs.length > 0
          ? qd.Tabs
          : [
              { name: 'index.html', type: 'HTML' },
              { name: 'styles.css', type: 'CSS' },
              { name: 'script.js', type: 'JS' }
            ];
        const codeValidation = {};
        tabs.forEach((t) => {
          codeValidation[t.name] = { template: '' };
        });

        const mappedQuestion = {
          Qn_name: q.question_id,
          Page_Name: qd.Page_Name || 'HTML/CSS Question',
          level: q.level || qd.level || 'level1',
          subtopic_id: q.sub_topic_id || qd.subtopic_id || '',
          type: 'coding',
          Tabs: tabs,
          Qn: qd.Qn || qd.question || '',
          requirements: qd.requirements || '',
          Code_Validation: qd.Code_Validation || codeValidation,
          defaulttemplate: qd.Template || qd.defaulttemplate || '',
          image_path: qd.image_path || '',
          video_path: qd.video_path || '',
          CreatedBy: qd.CreatedBy || '',
          CreatedOn: qd.CreatedON || '',
          LastUpdated: qd.LastUpdated || '',
          status: false,
          entered_ans: {},
          image_urls: qd.image_urls || [],
          currentFile: qd.currentFile || ''
        };

        // Persist to session storage for testing editor to consume
        try {
          sessionStorage.setItem('codingQuestions', JSON.stringify([mappedQuestion]));
          sessionStorage.setItem('currentQuestionIndex', '0');
        } catch (e) {
          // ignore storage errors
        }

        // Navigate to web editor route
        navigate('/coding-web');
      }
    } catch (error) {
      setErrorModal({ isOpen: true, message: error.message || 'Error occurred while navigating to coding questions' });
    }
  };

  const handleAddContent = async (content) => {
    try {
      // Validate required fields from content row
      if (!content.topic_id || !content.sub_topic_id) {
        setErrorModal({ 
          isOpen: true, 
          message: 'Topic and Sub Topic information is missing. Cannot proceed to content creation.' 
        });
        return;
      }

      if (!content.subject_id) {
        setErrorModal({ 
          isOpen: true, 
          message: 'Subject information is missing. Cannot proceed to content creation.' 
        });
        return;
      }

      const contentId = content.id || content.question_id;
      const chosenTypeRaw = String(content.content_type || content.category || filters.contentType || '').toLowerCase();
      const contentTypeMap = {
        [ContentTypeConstants.VIDEO.key]: ContentTypeConstants.VIDEO.key,
        [ContentTypeConstants.NOTES.key]: ContentTypeConstants.NOTES.key,
        [ContentTypeConstants.MCQ.key]: ContentTypeConstants.MCQ.key,
        [ContentTypeConstants.CODING.key]: ContentTypeConstants.CODING.key,
        [ContentTypeConstants.PROJECT_CODING.key]: ContentTypeConstants.PROJECT_CODING.key
      };
      
      // Get subject name from content row or subjects array
      const subjectName = content.subject_name || (subjects.find(subject => subject.subject_id === content.subject_id)?.subject_name || '');
      
      // Set session storage with content creation parameters
      sessionStorage.setItem('contentCreation_contentType', JSON.stringify(contentTypeMap[chosenTypeRaw]));
      sessionStorage.setItem('contentCreation_selectedDifficulty', JSON.stringify(content.level || 'level1'));
      sessionStorage.setItem('contentCreation_selectedSubTopic', JSON.stringify(content.sub_topic_id));
      sessionStorage.setItem('contentCreation_selectedTopic', JSON.stringify(content.topic_id));
      sessionStorage.setItem('contentCreation_subjectId', JSON.stringify(content.subject_id));
      sessionStorage.setItem('contentCreation_subjectName', JSON.stringify(subjectName));
      sessionStorage.setItem('contentCreation_projectId', JSON.stringify(projectId));
      if (project && project.project_name) {
        sessionStorage.setItem('contentCreation_projectName', JSON.stringify(project.project_name));
      }
      
      if (chosenTypeRaw === ContentTypeConstants.VIDEO.key) {
        sessionStorage.setItem('contentCreation_selectedVideo', JSON.stringify(contentId.toString()));
      } else if (chosenTypeRaw === ContentTypeConstants.NOTES.key) {
        sessionStorage.setItem('contentCreation_selectedNote', JSON.stringify(contentId.toString()));
      } else if (chosenTypeRaw === ContentTypeConstants.MCQ.key || chosenTypeRaw === ContentTypeConstants.CODING.key || chosenTypeRaw === ContentTypeConstants.PROJECT_CODING.key) {
        sessionStorage.setItem('contentCreation_selectedQuestion', JSON.stringify(contentId.toString()));
      }

      // For coding questions, pass editor from question_data / question_types_enum
      let editorValue = null;
      if (chosenTypeRaw === ContentTypeConstants.CODING.key || chosenTypeRaw === ContentTypeConstants.PROJECT_CODING.key) {
        const questionData = content.question_data || {};
        const editor =
          questionData.editor ||
          content.editor ||
          content.question_types_enum ||
          null;
        if (editor) {
          editorValue = editor;
          sessionStorage.setItem('contentCreation_selectedEditor', JSON.stringify(editor));
        } else {
          sessionStorage.removeItem('contentCreation_selectedEditor');
        }
      }
      
      // Store approval status and entity type if status is "assigned" to update after save
      if (content.approval_status?.toLowerCase() === 'assigned') {
        sessionStorage.setItem('contentCreation_isAssignedContent', 'true');
        sessionStorage.setItem('contentCreation_contentEntityType', JSON.stringify(chosenTypeRaw));
        sessionStorage.setItem('contentCreation_projectIdForStatusUpdate', JSON.stringify(projectId));
      } else {
        sessionStorage.removeItem('contentCreation_isAssignedContent');
        sessionStorage.removeItem('contentCreation_contentEntityType');
        sessionStorage.removeItem('contentCreation_projectIdForStatusUpdate');
      }
      
      // Store approval status for display in content creation page header
      sessionStorage.setItem('contentCreation_approvalStatus', JSON.stringify(content.approval_status || 'Unknown'));
      
      // Store batch_assigned status for disabling actions if project is batch-assigned
      sessionStorage.setItem('contentCreation_batchAssigned', batchAssigned ? 'true' : 'false');
      
      // Store description for project_coding questions
      if (chosenTypeRaw === 'project_coding' && content.description) {
        sessionStorage.setItem('contentCreation_projectCodingDescription', JSON.stringify(content.description));
      } else {
        sessionStorage.removeItem('contentCreation_projectCodingDescription');
      }
      
      // Store freezeFilters flag in sessionStorage to persist across navigation
      sessionStorage.setItem('contentCreation_freezeFilters', 'true');
      sessionStorage.setItem('contentCreation_navSource', 'project');
      sessionStorage.removeItem('contentCreation_isReviewReadOnly');
      sessionStorage.setItem('contentCreation_projectId', JSON.stringify(projectId));
      
      // Build navigation state
      const navData = {
        subject_id: content.subject_id,
        subject_name: subjectName,
        topic_id: content.topic_id,
        sub_topic_id: content.sub_topic_id,
        content_type: contentTypeMap[chosenTypeRaw],
        project_id: projectId,
        contentId: contentId.toString(),
        freezeFilters: true
      };
      if (editorValue) navData.editor = editorValue;
      navigate('/content-creation', { state: { data: navData } });
    } catch (error) {
      setErrorModal({ isOpen: true, message: 'Error occurred while navigating to content creation' });
    }
  };

  const handleOpenCommentPopup = (referenceType, referenceId) => {
    if (!referenceType || !referenceId) {
      setErrorModal({ isOpen: true, message: 'Unable to open comments. Invalid reference data.' });
      return;
    }
    
    setCommentPopup({
      isOpen: true,
      referenceType,
      referenceId: String(referenceId)
    });
  };

  const handleOpenClonePopup = (referenceType, referenceId) => {
    if (!referenceType || !referenceId) {
      setErrorModal({ isOpen: true, message: 'Unable to open clone popup. Invalid reference data.' });
      return;
    }
    
    setClonePopup({
      isOpen: true,
      referenceType,
      referenceId: String(referenceId)
    });
  };

  const getContentApprovalStatus = (content) =>
    String(content?.approval_status || '').toLowerCase();

  const isContentApproved = (content) => getContentApprovalStatus(content) === 'approved';

  const canToggleApproval = (content) => {
    const status = getContentApprovalStatus(content);
    // Selected content is locked; only admins can approve / unapprove
    return status !== 'selected' && userIsAdmin;
  };

  const resolveEntityType = (content) => {
    let entityType = String(
      content.content_type || content.question_type || content.category || filters.contentType || ''
    ).toLowerCase();

    if (entityType === 'questions') {
      entityType = (content.question_type || ContentTypeConstants.MCQ.key).toLowerCase();
    }

    const allowedTypes = [
      ContentTypeConstants.VIDEO.key,
      ContentTypeConstants.NOTES.key,
      ContentTypeConstants.MCQ.key,
      ContentTypeConstants.CODING.key,
      ContentTypeConstants.PROJECT_CODING.key,
    ];
    if (!allowedTypes.includes(entityType)) {
      entityType = ContentTypeConstants.VIDEO.key;
    }
    return entityType;
  };

  const patchLocalApprovalStatus = (contentIds, newStatus) => {
    const idSet = new Set(contentIds.map((id) => String(id)));
    setContents((prev) =>
      (prev || []).map((item) => {
        const itemId = String(item.id || item.question_id || '');
        if (!idSet.has(itemId)) return item;
        return {
          ...item,
          approval_status: newStatus,
        };
      })
    );
  };

  const handleApprovalStatusChange = async (content, newStatus, options = {}) => {
    const { silent = false, skipOptimistic = false, previousStatus: previousStatusOverride } = options;
    const previousStatus =
      previousStatusOverride !== undefined ? previousStatusOverride : content.approval_status;
    const nextStatus = String(newStatus || '').toLowerCase();
    const contentId = content.id || content.question_id;
    // When skipOptimistic, local UI was already updated; validate against original status
    const currentStatus = String(previousStatus || content.approval_status || '').toLowerCase();

    try {
      // Prevent changing "selected" status
      if (currentStatus === 'selected') {
        if (!silent) {
          setErrorModal({ 
            isOpen: true, 
            message: 'Cannot change approval status for "selected" content.' 
          });
        }
        return false;
      }
      
      // Prevent CC from changing status to/from approved
      if (!userIsAdmin && (
        nextStatus === 'approved' || currentStatus === 'approved'
      )) {
        if (!silent) {
          setErrorModal({ 
            isOpen: true, 
            message: 'Only Admin (AD) role can approve or unapprove content.' 
          });
        }
        return false;
      }

      // Optimistic local update — avoid table reload
      if (!skipOptimistic) {
        patchLocalApprovalStatus([contentId], nextStatus);
      }
      
      const entityType = resolveEntityType(content);
      
      const requestBody = {
        project_id: parseInt(projectId),
        entity_type: entityType,
        entity_id: String(contentId),
        approval_status: nextStatus
      };
      
      const response = await apiPut(`${config.API_BASE_URL}/api/update-approval-status/`, requestBody);
      
      if (response.data.success) {
        const confirmedStatus = response.data.approval_status || nextStatus;
        if (String(confirmedStatus).toLowerCase() !== nextStatus) {
          patchLocalApprovalStatus([contentId], confirmedStatus);
        }
        return true;
      }

      patchLocalApprovalStatus([contentId], previousStatus);
      if (!silent) {
        setErrorModal({ isOpen: true, message: response.data.message || 'Failed to update approval status' });
      }
      return false;
    } catch (err) {
      patchLocalApprovalStatus([contentId], previousStatus);
      if (!silent) {
        setErrorModal({ 
          isOpen: true, 
          message: err.response?.data?.detail || err.message || 'Failed to update approval status' 
        });
      }
      return false;
    }
  };

  const handleApprovalCheckboxChange = async (content, checked) => {
    if (!canToggleApproval(content)) return;
    const newStatus = checked ? 'approved' : 'uploaded';
    await handleApprovalStatusChange(content, newStatus);
  };

  const handleGlobalApprovalToggle = async (checked) => {
    const rows = getSortedContents().filter(canToggleApproval);
    const targets = checked
      ? rows.filter((content) => !isContentApproved(content))
      : rows.filter(isContentApproved);

    if (targets.length === 0) return;

    const newStatus = checked ? 'approved' : 'uploaded';
    const snapshots = targets.map((content) => ({
      content,
      contentId: content.id || content.question_id,
      previousStatus: content.approval_status,
    }));

    // Optimistic bulk update
    patchLocalApprovalStatus(
      snapshots.map((s) => s.contentId),
      newStatus
    );

    try {
      const results = await Promise.all(
        snapshots.map(({ content, previousStatus }) =>
          handleApprovalStatusChange(content, newStatus, {
            silent: true,
            skipOptimistic: true,
            previousStatus,
          })
        )
      );
      const failed = results.filter((ok) => !ok).length;
      if (failed > 0) {
        setErrorModal({
          isOpen: true,
          message: `Failed to update approval status for ${failed} item(s).`,
        });
      }
    } catch (err) {
      // Revert all optimistic changes for this batch
      setContents((prev) =>
        (prev || []).map((item) => {
          const itemId = String(item.id || item.question_id || '');
          const snapshot = snapshots.find((s) => String(s.contentId) === itemId);
          if (!snapshot) return item;
          return { ...item, approval_status: snapshot.previousStatus };
        })
      );
      setErrorModal({
        isOpen: true,
        message: err.response?.data?.detail || err.message || 'Failed to update approval status',
      });
    }
  };

  const renderApprovalStatusButton = (content) => {
    const status = content.approval_status?.toLowerCase() || '';
    const statusDisplay = content.approval_status || 'Unknown';
    
    // Disable button only when approval_status is "selected"
    const isDisabled = status === 'selected';
    
    // All buttons enabled (except when selected) and trigger handleAddContent
    let buttonProps = {
      disabled: isDisabled,
      onClick: () => handleAddContent(content),
      variant: 'contained',
      color: 'primary',
      size: 'small'
    };
    
    switch (status) {
      case 'selected':
        buttonProps.variant = 'contained';
        buttonProps.color = 'info';
        break;
        
      case 'assigned':
        buttonProps.variant = 'contained';
        buttonProps.color = 'warning';
        break;
        
      case 'uploaded':
        buttonProps.variant = 'contained';
        buttonProps.color = 'success';
        break;
        
      case 'approved':
        buttonProps.variant = 'contained';
        buttonProps.color = 'success';
        break;
        
      default:
        buttonProps.variant = 'outlined';
        buttonProps.color = 'default';
    }
    
    return (
      <Button
        {...buttonProps}
        title={isDisabled ? 'Content is locked - Project is batch-assigned' : ''}
        sx={{
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'capitalize',
          minWidth: '90px',
          ...(buttonProps.disabled && {
            opacity: 0.6,
            cursor: 'not-allowed'
          })
        }}
      >
        {statusDisplay}
      </Button>
    );
  };

  const renderStatus = (status) => {
    const isActive = status?.toLowerCase() !== 'inactive';
    return (
      <Chip
        label={status || 'Active'}
        size="small"
        sx={{
          backgroundColor: isActive ? '#d4edda' : '#f8d7da',
          color: isActive ? '#155724' : '#721c24',
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase',
          height: '24px'
        }}
      />
    );
  };

  const getTitleText = (content) => {
    const t = String(content.content_type || content.category || filters.contentType || '').toLowerCase();
    if (t === ContentTypeConstants.MCQ.key || t === ContentTypeConstants.CODING.key || t === ContentTypeConstants.PROJECT_CODING.key) {
      const questionData = content.question_data || {};
      // Prefer explicit 'question' field, then legacy 'Qn' or other possible keys
      return (
        questionData.question ||
        questionData.question_text ||
        questionData.Qn ||
        questionData.Qn_name ||
        questionData.Qn_text ||
        content.title ||
        'No question available'
      );
    }
    return content.title || 'Untitled';
  };

  const getCategoryIcon = (contentOrCategory) => {
    // Accept either the full content object or a category string
    let type = '';
    if (typeof contentOrCategory === 'string' || contentOrCategory === undefined) {
      type = String(contentOrCategory || filters.contentType || '').toLowerCase();
    } else {
      type = String(contentOrCategory.content_type || contentOrCategory.category || filters.contentType || '').toLowerCase();
    }
    switch (type) {
      case ContentTypeConstants.VIDEO.key: return <PiMonitorPlayBold title={ContentTypeConstants.VIDEO.label} size={20} />;
      case ContentTypeConstants.NOTES.key: return <SlNotebook title={ContentTypeConstants.NOTES.label} size={20} />;
      case ContentTypeConstants.MCQ.key: return <BsListTask title={ContentTypeConstants.MCQ.label} size={20} />;
      case ContentTypeConstants.CODING.key: return <LiaLaptopCodeSolid title={ContentTypeConstants.CODING.label} size={20} />;
      case ContentTypeConstants.PROJECT_CODING.key: return <AiOutlineFundProjectionScreen title={ContentTypeConstants.PROJECT_CODING.label} size={20} />;
      default:
        // Avoid returning raw objects as React children. Show a generic icon instead.
        return <span title={typeof contentOrCategory === 'string' ? contentOrCategory : 'Unknown'} style={{ fontSize: "18px" }}>❓</span>;
    }
  };

  const handleColumnMenuOpen = (event) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleToggleColumn = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const columnLabels = {
    sNo: 'S.no',
    phase: 'Phase',
    part: 'Part',
    task: 'Task',
    subTask: 'Sub Task',
    topic: 'Topic',
    subTopic: 'Sub Topic',
    category: 'Category',
    title: 'Title/Question',
    level: 'Level',
    duration: 'Duration',
    review: 'Review',
    approvalStatus: 'Approval Status',
    actions: 'Actions',
    status: 'Status'
  };

  // Sorting helpers
  const handleSort = (field) => {
    if (!field) return;
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getValueByColumn = (item, columnKey, index) => {
    if (!item) return '';
    switch (columnKey) {
      case 'sNo': return (pagination.page - 1) * pagination.limit + index + 1;
      case 'phase': return item.phase || '';
      case 'part': return item.part || '';
      case 'task': return item.task || '';
      case 'subTask': return item.sub_task || '';
      case 'topic': return item.topic || '';
      case 'subTopic': return item.sub_topic || '';
      case 'category': return String(item.content_type || item.category || '');
      case 'title':
        try { return getTitleText(item) || ''; } catch (e) { return ''; }
      case 'level': return item.level || '';
      case 'duration': {
        // If duration is already provided, use it
        if (item.duration) {
          return item.duration;
        }
        
        // Calculate duration for MCQ and Coding based on level if not provided
        const contentType = String(item.content_type || item.category || '').toLowerCase();
        const level = String(item.level || '').toLowerCase();
        
        if (contentType === ContentTypeConstants.MCQ.key) {
          // MCQ duration: level1 = 1 min, level2 = 1.5 min, level3 = 2 min
          if (level === 'level1') return 1;
          else if (level === 'level2') return 1.5;
          else if (level === 'level3') return 2;
        } else if (contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
          // Coding duration: level1 = 10 min, level2 = 20 min, level3 = 30 min
          if (level === 'level1') return 10;
          else if (level === 'level2') return 20;
          else if (level === 'level3') return 30;
        }
        
        return 0;
      }
      case 'review': return item.resolved || 0;
      case 'approvalStatus': return item.status || item.approval_status || '';
      case 'status': return item.status || '';
      default: return '';
    }
  };

  const getSortedContents = () => {
    if (!sortField) return contents || [];
    const arr = Array.isArray(contents) ? [...contents] : [];
    arr.sort((a, b) => {
      const ai = getValueByColumn(a, sortField, 0);
      const bi = getValueByColumn(b, sortField, 0);
      const aNum = typeof ai === 'number' || (!isNaN(Number(ai)) && ai !== '');
      const bNum = typeof bi === 'number' || (!isNaN(Number(bi)) && bi !== '');
      if (aNum && bNum) {
        const an = Number(ai || 0);
        const bn = Number(bi || 0);
        return sortDirection === 'asc' ? an - bn : bn - an;
      }
      const as = String(ai || '').toLowerCase();
      const bs = String(bi || '').toLowerCase();
      if (as === bs) return 0;
      return sortDirection === 'asc' ? (as > bs ? 1 : -1) : (as > bs ? -1 : 1);
    });
    return arr;
  };

  if (loading && !project) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {error && (
            <Alert severity="error" sx={{ m: 3 }}>
              {error}
            </Alert>
          )}

          {/* Filters Section - Sticky */}
          <div style={{
            backgroundColor: "#fff",
            padding: "15px 20px",
            borderBottom: "1px solid #e9ecef",
            flexShrink: 0,
            zIndex: 10
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <InlineHierarchicalFilter
                projects={projects}
                initialProjectId={projectId}
                initialFilters={{
                  approvalStatus: filters.approvalStatus,
                  contentType: filters.contentType,
                  subject: activeHierarchicalFilters.subject,
                  topic: activeHierarchicalFilters.topic,
                  subTopic: activeHierarchicalFilters.subTopic
                }}
                onProjectChange={(pid) => {
                  try { sessionStorage.setItem('projectId', JSON.stringify(pid)); } catch (e) { /* ignore */ }
                  navigate('/project-content', { state: { projectId: pid } });
                }}
                onApply={(appliedFilters) => handleApplyHierarchicalFilters(appliedFilters)}
              />
              {/* Column visibility control moved here so table can move up */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Toggle Column Visibility">
                  <IconButton
                    size="small"
                    onClick={handleColumnMenuOpen}
                    sx={{
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      p: 1
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={columnMenuAnchor}
                  open={Boolean(columnMenuAnchor)}
                  onClose={handleColumnMenuClose}
                  PaperProps={{
                    sx: {
                      maxWidth: '250px',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }
                  }}
                >
                  {Object.keys(columnLabels).map((columnKey) => (
                    <MenuItem
                      key={columnKey}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleColumn(columnKey);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: columnVisibility[columnKey] ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <Checkbox
                        checked={columnVisibility[columnKey]}
                        size="small"
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleColumn(columnKey);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>{columnLabels[columnKey]}</span>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>
          </div>

          {/* Content Table - Scrollable Only */}
          <Box sx={{ flex: 1, overflow: "hidden", padding: "20px", backgroundColor: "#fff", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {/* Column Visibility Toggle Button (moved to filters area) */}

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading content...
                </Typography>
              </Box>
            ) : contents.length === 0 ? (
              <Box sx={{ textAlign: "center", color: "#666", padding: "40px" }}>
                <Typography>No content available</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <TableContainer component={Paper} sx={{ border: '1px solid #dee2e6', borderRadius: '4px 4px 0 0', overflow: 'auto', flex: 1, minHeight: 0 }}>
                  <Table sx={{ minWidth: 650 }} aria-label="content table">
                    <TableHead sx={{ 
                      position: 'sticky', 
                      top: 0, 
                      zIndex: 10,
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #dee2e6'
                    }}>
                      <TableRow sx={{ 
                        backgroundColor: '#f8f9fa',
                        '&:hover': { backgroundColor: '#f8f9fa' }
                      }}>
                        {columnVisibility.sNo && (
                          <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px' }} onClick={() => handleSort('sNo')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}>
                              <span>#</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'sNo' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.phase && (
                          <TableCell onClick={() => handleSort('phase')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Phase</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'phase' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.part && (
                          <TableCell onClick={() => handleSort('part')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Part</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'part' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.task && (
                          <TableCell onClick={() => handleSort('task')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Task</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'task' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.subTask && (
                          <TableCell onClick={() => handleSort('subTask')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Sub Task</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'subTask' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.topic && (
                          <TableCell onClick={() => handleSort('topic')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Topic</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'topic' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.subTopic && (
                          <TableCell onClick={() => handleSort('subTopic')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Sub Topic</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'subTopic' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.category && (
                          <TableCell onClick={() => handleSort('category')} align="center" sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <span>Category</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'category' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.title && (
                          <TableCell onClick={() => handleSort('title')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Title/Question</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'title' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.level && (
                          <TableCell onClick={() => handleSort('level')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Level</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'level' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.duration && (
                          <TableCell onClick={() => handleSort('duration')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Duration</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'duration' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.review && (
                          <TableCell onClick={() => handleSort('review')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Review</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'review' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.approvalStatus && (
                          <TableCell
                            onClick={() => handleSort('approvalStatus')}
                            sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {(() => {
                                const toggleableRows = getSortedContents().filter(canToggleApproval);
                                const approvedCount = toggleableRows.filter(isContentApproved).length;
                                const allChecked = toggleableRows.length > 0 && approvedCount === toggleableRows.length;
                                const someChecked = approvedCount > 0 && approvedCount < toggleableRows.length;
                                return (
                                  <Checkbox
                                    size="small"
                                    checked={allChecked}
                                    indeterminate={someChecked}
                                    disabled={toggleableRows.length === 0 || loading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleGlobalApprovalToggle(e.target.checked);
                                    }}
                                    inputProps={{ 'aria-label': 'Approve or unapprove all visible content' }}
                                    sx={{ p: 0.25 }}
                                  />
                                );
                              })()}
                              <span>Approval Status</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'approvalStatus' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                        {columnVisibility.actions && <TableCell align="center" sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px' }}>Actions</TableCell>}
                        {columnVisibility.status && (
                          <TableCell onClick={() => handleSort('status')} sx={{ fontWeight: 700, fontSize: '13px', color: '#333', whiteSpace: 'nowrap', padding: '16px 12px', cursor: 'pointer' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>Status</span>
                              <span style={{ fontSize: 11 }}>{sortField === 'status' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getSortedContents().map((content, index) => {
                        const contentId = content.id || content.question_id;
                        const rowType = String(content.content_type || content.category || filters.contentType || '').toLowerCase();
                        return (
                          <TableRow 
                            key={contentId}
                            sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                          >
                            {columnVisibility.sNo && (
                              <TableCell sx={{ fontSize: "12px" }}>
                                {(pagination.page - 1) * pagination.limit + index + 1}
                              </TableCell>
                            )}
                            {columnVisibility.phase && <TableCell sx={{ fontSize: "12px" }}>{content.phase || '-'}</TableCell>}
                            {columnVisibility.part && <TableCell sx={{ fontSize: "12px" }}>{content.part || '-'}</TableCell>}
                            {columnVisibility.task && <TableCell sx={{ fontSize: "12px" }}>{content.task || '-'}</TableCell>}
                            {columnVisibility.subTask && <TableCell sx={{ fontSize: "12px" }}>{content.sub_task || '-'}</TableCell>}
                            {columnVisibility.topic && <TableCell sx={{ fontSize: "12px" }}>{content.topic || '-'}</TableCell>}
                            {columnVisibility.subTopic && <TableCell sx={{ fontSize: "12px" }}>{content.sub_topic || '-'}</TableCell>}
                            {columnVisibility.category && <TableCell align="center">{getCategoryIcon(content)}</TableCell>}
                            {columnVisibility.title && (
                              <TableCell 
                                sx={{ 
                                  fontSize: "12px", 
                                  maxWidth: "200px", 
                                  overflow: "hidden", 
                                  textOverflow: "ellipsis", 
                                  whiteSpace: "nowrap" 
                                }} 
                                title={getTitleText(content)}
                              >
                                {getTitleText(content)}
                              </TableCell>
                            )}
                            {columnVisibility.level && <TableCell sx={{ fontSize: "12px" }}>{content.level || '-'}</TableCell>}
                            {columnVisibility.duration && (
                              <TableCell sx={{ fontSize: "12px" }}>
                                {(() => {
                                  // If duration is already provided, use it
                                  if (content.duration) {
                                    return `${content.duration} min`;
                                  }
                                  
                                  // Calculate duration for MCQ and Coding based on level if not provided
                                  const contentType = String(content.content_type || content.category || '').toLowerCase();
                                  const level = String(content.level || '').toLowerCase();
                                  
                                  if (contentType === ContentTypeConstants.MCQ.key || contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key){
                                    let calculatedDuration = null;
                                    
                                    if (contentType === ContentTypeConstants.MCQ.key) {
                                      // MCQ duration: level1 = 1 min, level2 = 1.5 min, level3 = 2 min
                                      if (level === 'level1') calculatedDuration = 1;
                                      else if (level === 'level2') calculatedDuration = 1.5;
                                      else if (level === 'level3') calculatedDuration = 2;
                                    } else if (contentType === ContentTypeConstants.CODING.key || contentType === ContentTypeConstants.PROJECT_CODING.key) {
                                      // Coding duration: level1 = 10 min, level2 = 20 min, level3 = 30 min
                                      if (level === 'level1') calculatedDuration = 10;
                                      else if (level === 'level2') calculatedDuration = 20;
                                      else if (level === 'level3') calculatedDuration = 30;
                                    }
                                    
                                    if (calculatedDuration !== null) {
                                      return `${calculatedDuration} min`;
                                    }
                                  }
                                  
                                  return '-';
                                })()}
                              </TableCell>
                            )}
                            {columnVisibility.review && (
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleCheckReview(content)}
                                  sx={{ fontSize: "12px", padding: "4px 8px" }}
                                >
                                  Check
                                </Button>
                              </TableCell>
                            )}
                            {columnVisibility.approvalStatus && (
                              <TableCell sx={{ fontSize: "12px" }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Checkbox
                                    size="small"
                                    checked={isContentApproved(content)}
                                    disabled={!canToggleApproval(content) || loading}
                                    onChange={(e) =>
                                      handleApprovalCheckboxChange(content, e.target.checked)
                                    }
                                    inputProps={{
                                      'aria-label': isContentApproved(content)
                                        ? 'Unapprove content'
                                        : 'Approve content',
                                    }}
                                    sx={{ p: 0.25 }}
                                    title={
                                      getContentApprovalStatus(content) === 'selected'
                                        ? 'Cannot change approval for selected content'
                                        : !userIsAdmin
                                          ? 'Only Admin can approve or unapprove'
                                          : isContentApproved(content)
                                            ? 'Unapprove'
                                            : 'Approve'
                                    }
                                  />
                                  {renderApprovalStatusButton(content)}
                                </Box>
                              </TableCell>
                            )}
                            {columnVisibility.actions && (
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                  <Tooltip title="Comments">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenCommentPopup(
                                        rowType === 'video' ? 'video' : rowType === 'notes' ? 'notes' : 'question',
                                        contentId
                                      )}
                                      sx={{ position: 'relative' }}
                                    >
                                      <CommentIcon fontSize="small" />
                                      {content.resolved > 0 && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: '-6px',
                                            right: '-6px',
                                            backgroundColor: '#dc3545',
                                            color: '#fff',
                                            borderRadius: '999px',
                                            padding: '0 5px',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            minWidth: '18px',
                                            height: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          {content.resolved}
                                        </Box>
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Clone Content">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenClonePopup(
                                        rowType === 'video' ? 'video' : rowType === 'notes' ? 'notes' : 'question',
                                        contentId
                                      )}
                                      sx={{ color: '#6c757d' }}
                                    >
                                      <FileCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            )}
                            {columnVisibility.status && (
                              <TableCell sx={{ fontSize: "12px" }}>
                                {renderStatus(content.status)}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 15, 25, 50]}
                  component="div"
                  count={pagination.total}
                  rowsPerPage={pagination.limit}
                  page={pagination.page - 1}
                  onPageChange={(event, newPage) => {
                    setPagination(prev => ({ ...prev, page: newPage + 1 }));
                  }}
                  onRowsPerPageChange={(event) => {
                    const newLimit = parseInt(event.target.value, 10);
                    setPagination(prev => ({
                      ...prev,
                      limit: newLimit,
                      page: 1
                    }));
                  }}
                  sx={{
                    backgroundColor: '#fff',
                    borderTop: '1px solid #dee2e6',
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                    borderLeft: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderBottom: '1px solid #dee2e6',
                    '& .MuiTablePagination-toolbar': {
                      padding: '10px 16px',
                      minHeight: '56px',
                      justifyContent: 'space-between'
                    },
                    '& .MuiTablePagination-selectLabel': {
                      marginTop: 0,
                      marginBottom: 0,
                      fontSize: '13px',
                      fontWeight: 500
                    },
                    '& .MuiTablePagination-displayedRows': {
                      marginTop: 0,
                      marginBottom: 0,
                      fontSize: '13px',
                      textAlign: 'center'
                    },
                    '& .MuiTablePagination-select': {
                      fontSize: '13px',
                      padding: '6px 24px 6px 8px',
                      marginLeft: '12px'
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: '16px'
                    },
                    '& .MuiIconButton-root': {
                      padding: '8px'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </div>

      <ConfirmDialog
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
        variant="alert"
        severity="error"
      />

      <VideoPreviewModal
        isOpen={videoPreviewModal.isOpen}
        onClose={() => setVideoPreviewModal({ isOpen: false, video: null })}
        video={videoPreviewModal.video}
      />

      <NotesPreviewModal
        isOpen={notesPreviewModal.isOpen}
        onClose={() => setNotesPreviewModal({ isOpen: false, notes: null })}
        notes={notesPreviewModal.notes}
      />

      <MCQPreviewModal
        isOpen={mcqPreviewModal.isOpen}
        onClose={() => setMcqPreviewModal({ isOpen: false, mcq: null })}
        mcq={mcqPreviewModal.mcq}
      />

      {/* Comment Popup */}
      <CommentPopup
        isOpen={commentPopup.isOpen}
        onClose={() => setCommentPopup({ isOpen: false, referenceType: null, referenceId: null })}
        referenceType={commentPopup.referenceType}
        referenceId={commentPopup.referenceId}
        userEmail={sessionStorage.getItem('Email')}
        userName={sessionStorage.getItem('Name') || 'User'}
      />

      {/* Clone Popup */}
      <ClonePopup
        isOpen={clonePopup.isOpen}
        onClose={() => setClonePopup({ isOpen: false, referenceType: null, referenceId: null })}
        referenceType={clonePopup.referenceType}
        referenceId={clonePopup.referenceId}
        userEmail={sessionStorage.getItem('Email')}
        userName={sessionStorage.getItem('Name') || 'User'}
      />
    </div>
  );
}

export default ProjectContentBank;

